/**
 * SiteProvider — SPA architectural convergence plan §6.3.
 *
 * "SiteContext owns the authorized API list and active site;
 * assignment removal aborts streams, clears site caches, and
 * reconciles selection."
 *
 * The sites list comes exclusively from the global
 * `nexsoss-api-server` (`GET /api/v1/sites`), gated by the caller's
 * ZITADEL JWT. There is no longer a runtime-config seed or a Zenoh
 * discovery path; the static seed was removed (plan §6.4).
 *
 * If the API returns an empty list, the dropdown shows no sites and
 * every per-site hook is disabled (`enabled: false` on its query).
 * The runtime-config seed is gone, so a returned-empty list is the
 * authoritative "user has no assignments" state.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSites } from '../api/hooks/useSites';
import { fetchSseTransport } from '../api/fetch-sse';
import { setTelemetrySite } from '../observability/telemetry';
import { SiteContext, type ActiveSite } from './SiteContext';
import type { SiteId, SiteSummary } from '../api/v1';

/**
 * Tear down all SPA-side state for a site whose assignment was
 * revoked: cancel in-flight per-site queries, drop cached slices,
 * and close the live SSE handle. Plan §6.3 mandates all three.
 */
function teardownSite(qc: ReturnType<typeof useQueryClient>, removedSite: SiteId) {
  qc.cancelQueries({ queryKey: ['v1', removedSite], exact: false });
  qc.removeQueries({ queryKey: ['v1', removedSite], exact: false });
  fetchSseTransport().disconnect(removedSite);
}

/** Frozen singleton, not an inline `[]`: see the memo below. */
const EMPTY_SITES: ReadonlyArray<SiteSummary> = [];

export function SiteProvider({ children }: { children: ReactNode }) {
  const sitesQuery = useSites();
  // A fresh `[]` on every render is a new identity, and this value feeds an
  // effect, a callback and a memo below -- so an idle query re-ran all three
  // every render, and the context value they build changed with them, which
  // re-rendered every consumer of the site context in the app.
  const sites: ReadonlyArray<SiteSummary> = useMemo(
    () => sitesQuery.data ?? EMPTY_SITES,
    [sitesQuery.data],
  );
  const qc = useQueryClient();

  const [site, setSiteState] = useState<ActiveSite | null>(() => {
    const initial = sites[0];
    return initial ? { id: initial.site, label: initial.label } : null;
  });

  // Reconcile: when the API returns a new site list (login, logout,
  // assignment change), drop the active site if it is no longer in
  // the list. Plan §6.3: "assignment removal aborts streams, clears
  // site caches, and reconciles selection."
  useEffect(() => {
    if (!site) {
      const first = sites[0];
      if (first) setSiteState({ id: first.site, label: first.label });
      return;
    }
    const stillThere = sites.some((s) => s.site === site.id);
    if (!stillThere) {
      // Plan §6.3: abort streams, clear caches, then reconcile
      // selection. We teardown BEFORE switching so the next selected
      // site's hooks start with an empty cache.
      teardownSite(qc, site.id);
      const first = sites[0];
      setSiteState(first ? { id: first.site, label: first.label } : null);
    }
  }, [sites, site, qc]);

  const setSite = useCallback((siteId: SiteId) => {
    const found = sites.find((s) => s.site === siteId);
    if (found) setSiteState({ id: found.site, label: found.label });
  }, [sites]);

  useEffect(() => {
    setTelemetrySite(site?.id ?? null);
  }, [site]);

  const reconcile = useCallback(() => {
    void sitesQuery.refetch();
  }, [sitesQuery]);

  const value = useMemo(
    () => ({ sites, site, setSite, reconcile }),
    [sites, site, setSite, reconcile],
  );

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}